import { getSpaceCode as getRequestSpaceCode, setSpaceCode as setRequestSpaceCode } from '@libs/rest-client';
import { useTry } from '@ps-smart-cp/ppfw-client';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { MAIN_SPACE_CODE } from '../constants/Space';
import { Space } from '../models';
import { createSpace, fetchCurrentSpace, fetchSpaces } from '../repositories';
import { findSpaceByName } from '../services';
import type { SpaceIdentity } from '../types';

interface SpaceContextProps extends SpaceIdentity {
  /** 台帳の選択候補。{@link SpaceContextProps.loadSpaceOptions} を呼ぶまでは空。 */
  spaceOptions: Space[];
  /**
   * 選択候補（`GET /space`）を取得する。候補を使うのは台帳切替の UI だけなので、provider は
   * 先に取らず、その UI が mount したときに呼ぶ。
   */
  loadSpaceOptions: () => Promise<void>;
  /**
   * 台帳を切り替える。リクエストの Space code を更新し、`GET /space/current` で現在の台帳を取り直す。
   *
   * <p>会社情報・会計方針設定・設定帳簿・マスタ設定・カスタムフィールド設定・繰越情報・運用情報は
   * 台帳で区別しない（どの台帳でも共通）ので、画面の再描画（`reloadApp()`）はしない。
   */
  setSpace: (space: Space) => Promise<void>;
  /**
   * spaceCode を指定して台帳を切り替える（申請明細照会の `?space=` に合わせるときに使う）。
   * 存在しないコードは server が本台帳に置き換えるので、切替後の `spaceCode` は指定と違うことがある。
   * 取得に失敗したら false を返し、リクエストの Space code は切替前に戻す。
   */
  switchSpaceByCode: (spaceCode: string) => Promise<boolean>;
  /** 台帳を作成して、作成した台帳へ切り替える。 */
  addSpace: (name: string) => Promise<void>;
  /** 本台帳へ切り替える。運用取り消しが成功した後に呼ぶ。 */
  resetToMainSpace: () => Promise<void>;
  /**
   * 本台帳へ切り替え、選択候補を取り直す。繰越が成功した後（処理月度が変わったとき）に呼ぶ。
   * 繰越の後は server が本台帳以外を候補から外すため。選択候補を読み込む前（運用開始前）は候補を取らない。
   */
  resetAfterCarryOver: () => Promise<void>;
}

const SpaceContext = createContext<SpaceContextProps>({
  spaceCode: '',
  spaceName: '',
  spaceOptions: [],
  loadSpaceOptions: async () => {},
  setSpace: async () => {},
  switchSpaceByCode: async () => false,
  addSpace: async () => {},
  resetToMainSpace: async () => {},
  resetAfterCarryOver: async () => {},
});

function SpaceProvider({ children }: { children: ReactNode }) {
  const [space, setSpaceState] = useState<Space>();
  // 初回の取得が終わったか（成否を問わない）。失敗しても FA 全体を空白にしないため、終わった時点で描画する。
  const [isInitialized, setIsInitialized] = useState(false);
  const [spaceOptions, setSpaceOptions] = useState<Space[]>([]);
  // 選択候補を読み込んだことがあるか（台帳切替の UI が mount したか）。
  const isSpaceOptionsLoadedRef = useRef(false);

  const { tryRequest } = useTry();

  // `GET /space/current` は自動付与された Space code を解決する（未指定・不正なら本台帳）。
  // 切替後もここで取り直し、server が解決した台帳（不正なコードなら本台帳）に合わせる。
  // 取得できたかを返す（`tryRequest` は例外を握りつぶすため）。
  const fetchSpace = useCallback(async () => {
    let isFetched = false;
    await tryRequest(async () => {
      const current = Space.fromRaw(await fetchCurrentSpace());
      setRequestSpaceCode(current.spaceCode);
      setSpaceState(current);
      isFetched = true;
    });
    return isFetched;
  }, []);

  const loadSpaceOptions = useCallback(async () => {
    isSpaceOptionsLoadedRef.current = true;
    await tryRequest(async () => {
      setSpaceOptions(Space.fromRaws(await fetchSpaces()));
    });
  }, []);

  // 取得に失敗したらリクエストの Space code を切替前に戻す（context の台帳とずれないように）。
  const switchSpaceByCode = useCallback(
    async (spaceCode: string) => {
      const previous = getRequestSpaceCode();
      setRequestSpaceCode(spaceCode);
      const isSwitched = await fetchSpace();
      if (!isSwitched) {
        setRequestSpaceCode(previous);
      }
      return isSwitched;
    },
    [fetchSpace],
  );

  const setSpace = useCallback(
    async (next: Space) => {
      await switchSpaceByCode(next.spaceCode);
    },
    [switchSpaceByCode],
  );

  // `clearSpaceCode()` で key ごと消すと、server の scope が付かないため本台帳のコードを明示する。
  const resetToMainSpace = useCallback(async () => {
    await switchSpaceByCode(MAIN_SPACE_CODE);
  }, [switchSpaceByCode]);

  const resetAfterCarryOver = useCallback(async () => {
    await resetToMainSpace();
    if (isSpaceOptionsLoadedRef.current) {
      await loadSpaceOptions();
    }
  }, [resetToMainSpace, loadSpaceOptions]);

  const addSpace = useCallback(
    async (name: string) =>
      await tryRequest(async () => {
        await createSpace(name);
        const spaces = Space.fromRaws(await fetchSpaces());
        setSpaceOptions(spaces);
        const created = findSpaceByName(spaces, name);
        if (created) {
          await setSpace(created);
        }
      }),
    [setSpace],
  );

  useEffect(() => {
    void fetchSpace().finally(() => setIsInitialized(true));
  }, [fetchSpace]);

  if (!isInitialized) {
    return null;
  }

  // 取得に失敗したときは空の Space で描画する（リクエストには Space code を付けず、server の既定＝本台帳になる）。
  const current = space ?? Space.init();

  return (
    <SpaceContext.Provider
      value={{
        spaceCode: current.spaceCode,
        spaceName: current.spaceName,
        spaceOptions,
        loadSpaceOptions,
        setSpace,
        switchSpaceByCode,
        addSpace,
        resetToMainSpace,
        resetAfterCarryOver,
      }}
    >
      {children}
    </SpaceContext.Provider>
  );
}

function useSpace() {
  return useContext(SpaceContext);
}

export { SpaceProvider, useSpace };
